/**
 * ActiveShares.jsx
 *
 * Displays list of active document shares with management options.
 *
 * Branding: H Nawab (#CC0202 red, #262626 dark)
 */

import React, { useState, useEffect } from "react";
import {
  Link2,
  Copy,
  Trash2,
  Clock,
  Eye,
  Lock,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Users,
  Building2,
  Scale,
  Shield,
  User,
} from "lucide-react";

const RECIPIENT_ICONS = {
  service_provider: Users,
  buyer: Building2,
  legal: Scale,
  insurance: Shield,
  custom: User,
};

function ShareCard({ share, onRevoke, onCopy }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const RecipientIcon = RECIPIENT_ICONS[share.recipientType] || User;

  const handleCopy = () => {
    const url = `${window.location.origin}${share.shareUrl}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (onCopy) onCopy(share);
  };

  const handleRevoke = async () => {
    if (!window.confirm("Revoke this share? The link will no longer work.")) return;

    setRevoking(true);
    try {
      await onRevoke(share.id);
    } finally {
      setRevoking(false);
    }
  };

  const isInactive = share.isExpired || share.hasExceededAccesses;

  const getStatusBadge = () => {
    if (share.isExpired) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <AlertTriangle className="w-3 h-3" />
          Expired
        </span>
      );
    }
    if (share.hasExceededAccesses) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <AlertTriangle className="w-3 h-3" />
          Max views reached
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
        <Check className="w-3 h-3" />
        Active
      </span>
    );
  };

  return (
    <div className={`border rounded-xl overflow-hidden ${isInactive ? "opacity-60" : ""}`}>
      {/* Main Row */}
      <div className="p-3 flex items-center gap-3">
        {/* Icon */}
        <div className="p-2 rounded-lg bg-gray-100">
          <RecipientIcon className="w-4 h-4 text-gray-500" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-hn-dark truncate">
              {share.recipientName || share.recipientType?.replace("_", " ") || "Share"}
            </p>
            {share.hasPassword && (
              <Lock className="w-3.5 h-3.5 text-gray-400" title="Password protected" />
            )}
          </div>
          <p className="text-xs text-gray-500">
            {share.documentIds.length} document{share.documentIds.length !== 1 ? "s" : ""} •{" "}
            {share.accessCount} view{share.accessCount !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Status */}
        {getStatusBadge()}

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopy}
            disabled={isInactive}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Copy link"
          >
            {copied ? (
              <Check className="w-4 h-4 text-emerald-600" />
            ) : (
              <Copy className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <button
            onClick={handleRevoke}
            disabled={revoking}
            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
            title="Revoke share"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-3 pb-3 pt-0 border-t border-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
            <div>
              <p className="text-gray-500 mb-0.5">Created</p>
              <p className="text-gray-700">
                {new Date(share.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-gray-500 mb-0.5">Expires</p>
              <p className="text-gray-700">
                {share.expiresAt
                  ? new Date(share.expiresAt).toLocaleDateString()
                  : "Never"}
              </p>
            </div>
            <div>
              <p className="text-gray-500 mb-0.5">Max Views</p>
              <p className="text-gray-700">
                {share.maxAccesses
                  ? `${share.accessCount}/${share.maxAccesses}`
                  : "Unlimited"}
              </p>
            </div>
            <div>
              <p className="text-gray-500 mb-0.5">Last Accessed</p>
              <p className="text-gray-700">
                {share.lastAccessedAt
                  ? new Date(share.lastAccessedAt).toLocaleString()
                  : "Never"}
              </p>
            </div>
          </div>

          {share.recipientEmail && (
            <p className="text-xs text-gray-500 mt-2">
              Sent to: {share.recipientEmail}
            </p>
          )}

          <div className="flex items-center gap-2 mt-3">
            <a
              href={share.shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-hn-red font-medium hover:underline flex items-center gap-1"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Preview link
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ActiveShares({ propertyId, refreshTrigger }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    fetchShares();
  }, [propertyId, refreshTrigger]);

  const fetchShares = async () => {
    if (!propertyId) return;

    try {
      setLoading(true);
      const res = await fetch(`/api/shares/${propertyId}`);
      if (res.ok) {
        const data = await res.json();
        setShares(data);
      }
    } catch (err) {
      console.error("Error fetching shares:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (shareId) => {
    try {
      const res = await fetch(`/api/shares/${shareId}`, { method: "DELETE" });
      if (res.ok) {
        setShares((prev) => prev.filter((s) => s.id !== shareId));
      }
    } catch (err) {
      console.error("Error revoking share:", err);
    }
  };

  // Don't show if no shares
  if (!loading && shares.length === 0) return null;

  const activeShares = shares.filter((s) => !s.isExpired && !s.hasExceededAccesses);
  const inactiveShares = shares.filter((s) => s.isExpired || s.hasExceededAccesses);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-hn-red" />
          <h3 className="text-lg font-semibold text-hn-dark">Active Shares</h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {activeShares.length}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {expanded && (
        <div className="px-4 pb-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {activeShares.map((share) => (
                <ShareCard
                  key={share.id}
                  share={share}
                  onRevoke={handleRevoke}
                />
              ))}

              {inactiveShares.length > 0 && (
                <>
                  <p className="text-xs text-gray-500 font-medium mt-4 mb-2">
                    Expired / Max Views Reached
                  </p>
                  {inactiveShares.map((share) => (
                    <ShareCard
                      key={share.id}
                      share={share}
                      onRevoke={handleRevoke}
                    />
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
